import { createClassName, createStyleHelper } from '../../utils/class-names';
import type { FCWithChildren } from '../../utils/component.interface';
import { ContextLink } from '../context-link/context-link';
import { Icon } from '../icon/icon';

import styles from './button.module.scss';

const style = createStyleHelper(styles, 'button');

export enum ButtonSize {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    X_LARGE = 'x-large',
}

export enum Rounding {
    NONE = 'rounded-none',
    SMALL = 'rounded-small',
    NORMAL = 'rounded',
    BIG = 'rounded-big',
}

export enum ButtonColorVariant {
    DEFAULT = 'default',
    SOLID = 'solid',
    SOLID_DISABLED = 'solid-disabled',
}

export interface IButtonProps {
    className?: string;
    size: ButtonSize;
    hollow?: boolean;
    blank?: boolean;
    light?: boolean;
    warning?: boolean;
    rounding?: Rounding;
    disabled?: boolean;
    buttonColorVariant?: ButtonColorVariant;
    href?: string;
    type?: 'submit' | 'button';
    loading?: boolean;
    rawHref?: boolean;
    onClick?: () => void;
}

export const Button: FCWithChildren<IButtonProps> = ({
    className,
    size,
    hollow,
    blank,
    light,
    loading = false,
    rawHref = false,
    warning,
    rounding = Rounding.NORMAL,
    disabled,
    buttonColorVariant = ButtonColorVariant.DEFAULT,
    href,
    children,
    type = 'button',
    onClick,
    ...restProps
}) => {
    const classes = createClassName([
        className,
        style('', {
            [size]: true,
            hollow: hollow || !!light,
            light: !!light,
            loading: !!loading,
            warning: !!warning,
            [rounding]: true,
            disabled: !!disabled,
            [buttonColorVariant]: true,
        }),
    ]);

    const buttonLoader = (
        <div className={style('loader')}>
            <Icon className={style('icon')} name={'spinner'} />
        </div>
    );

    if (href) {
        return (
            <ContextLink
                to={href}
                rawHref={rawHref}
                targetBlank={blank}
                className={classes}
                onClick={onClick}
                {...restProps}
            >
                {buttonLoader}
                {children}
            </ContextLink>
        );
    }

    return (
        <button
            type={type}
            onClick={onClick}
            className={classes}
            disabled={disabled}
            {...restProps}
        >
            {buttonLoader}
            {children}
        </button>
    );
};
